import { toJS, observable, computed, action, ObservableMap } from 'mobx';
import { clone } from 'utils';

class FormStore {
  @observable values = null;
  @observable pristine = true;
  @observable isFormSubmitted = false;
  @observable isBeingSubmitted = false;

  @observable errors = {};
  @observable fieldValidity = new ObservableMap();

  fieldValidationRules = new Map();
  funcs = null;
  initialValues = null;

  @observable globalValidityFlag = false;

  backValuesUp() {
    if (this.values) {
      this.initialValues = clone(this.values);
    }
  }

  constructor(values, funcs = {}) {
    this.values = values;
    this.funcs = funcs;

    this.backValuesUp();
  }

  @computed
  get isValid() {
    return this.globalValidityFlag && !this.fieldValidity.values().some((v) => !v);
  }

  @action
  setFormToSubmittedState() {
    this.isFormSubmitted = true;
  }

  @action
  resetFormFromSubmittedState(globalValidityFlag = true) {
    this.isFormSubmitted = false;
    this.globalValidityFlag = globalValidityFlag;
  }

  setFieldValidationRules(fieldName, rules) {
    this.fieldValidationRules.set(fieldName, rules || []);
  }

  clearFieldValidationRules(fieldName) {
    this.fieldValidationRules.clear();
  }

  checkFieldValidity(fieldName, fieldValue = null) {
    const rules = this.fieldValidationRules.get(fieldName) || [];
    let result = { valid: true };

    for (let i = 0; result.valid && i < rules.length; i++) {
      result = rules[i](fieldValue);
    }

    return result;
  }

  validateSync(fieldName) {
    const validationRulesResult = this.checkFieldValidity(fieldName, this.values[fieldName]);

    const isValid = validationRulesResult.valid;
    this.errors[fieldName] = validationRulesResult.message;

    this.fieldValidity.set(fieldName, isValid);
    if(this.funcs.setFieldValidity) {
      this.funcs.setFieldValidity(fieldName, this.fieldValidity);
    }

    let externalValidationResult = true;
    if (isValid) {
      const validateSync = this.funcs.validateSync;
      externalValidationResult = !validateSync || validateSync(this.values, this.errors, fieldName);
    }

    this.globalValidityFlag = externalValidationResult;
  }

  @action
  onChangeFieldValue(name, value, force = false) {
    const isChanged = this.values[name] !== value;
    const onChangeFieldCustom = this.funcs.onChangeFieldValue;
    if (isChanged || force) {
      if (onChangeFieldCustom) {
        onChangeFieldCustom(name, value, this.values)
      } else {
        this.values[name] = value;
      }

      this.validateSync(name);
      this.pristine = false;
    }
  }

  resetCaptcha() {
    window.grecaptcha && window.grecaptcha.reset();
    this.onChangeFieldValue('captchaVerified', false);
  }

  clearValues() {
    if (!this.initialValues) {
      return;
    }

    for (var key in this.initialValues) {
      if (this.initialValues.hasOwnProperty(key)) {
        this.values[key] = this.initialValues[key];
      }
    }
  }

  clearErorrs() {
    this.errors = {};
    this.fieldValidity.clear();
  }

  @action
  setInvalidForm() {
    this.clearErorrs();
    this.pristine = true;
  }

  getFormValues() {
    return toJS(this.values);
  }

  setFormValues(newValues = {}, initial = false) {
    Object.keys(newValues).forEach((key) => {
      if (key in this.values) {
        this.onChangeFieldValue(key, newValues[key], initial);
      }
    });

    if (initial) {
      this.backValuesUp();
    }
  }
}

export { FormStore };



// WEBPACK FOOTER //
// ./src/common/core/forms/FormStore.js